import { getFY } from '../../utils/calculation';
import { getTotalInvestmentsSum } from '../../utils/utils';

export const calculateSum = (
  records,
  financialType: string,
  investmentType: string,
): number => {
  return records.reduce((sum, record) => {
    const financialData = record[financialType];
    if (financialData && typeof financialData[investmentType] === 'number') {
      sum += financialData[investmentType] || 0;
    } else if (
      financialData &&
      typeof financialData[investmentType] === 'string'
    ) {
      const parsedValue = parseFloat(financialData[investmentType] || '0');
      sum += isNaN(parsedValue) ? 0 : parsedValue;
    }
    return sum;
  }, 0);
};

export const calculateSumByInvestmentType = (
  records,
  investmentType: string,
  financialType: string,
): number => {
  return records.reduce((sum, record) => {
    const financialData = record[financialType];
    if (financialData && typeof financialData[investmentType] === 'number') {
      sum += financialData[investmentType] || 0;
    } else if (
      financialData &&
      typeof financialData[investmentType] === 'string'
    ) {
      const parsedValue = parseFloat(financialData[investmentType] || '0');
      sum += isNaN(parsedValue) ? 0 : parsedValue;
    }
    return sum;
  }, 0);
};

export const returnEmptyInvestmentItem = () => ({
  fiscalYear: getFY(),
  landCost: 0,
  propertyInvestment: 0,
  retailCost: 0,
  retailInvestment: 0,
  totalInvestment: 0,
});

export const getCurrencyRate = (
  countryCode: string,
  currencyArray: object,
  fy?: string,
) => {
  const currencyObject = currencyArray?.[fy] || currencyArray?.[getFY()];
  const country = currencyObject?.find(
    (item) => item.countryId === countryCode,
  );
  return country?.currencyRate || 1;
};

export const returnTotals = (investments) => {
  if (!investments) {
    return;
  }
  const land = calculateSum(investments, 'BUDGET', 'landCost');
  const propertyInvestment = calculateSum(
    investments,
    'BUDGET',
    'propertyInvestment',
  );
  const retailInvestment = calculateSum(
    investments,
    'BUDGET',
    'retailInvestment',
  );
  const retailCost = calculateSum(investments, 'BUDGET', 'retailCost');
  const totalBudget = getTotalInvestmentsSum(
    land,
    propertyInvestment,
    retailCost,
    retailInvestment,
  );

  const calculateByType = (type, category) =>
    calculateSumByInvestmentType(investments, type, category);

  const round2 = (n: number) => Number(n.toFixed(2));

  return {
    totalLand: land,
    totalPropertyInv: propertyInvestment,
    totalRetailInv: retailInvestment,
    totalRetail: retailCost,
    totalProjectBudget: totalBudget,

    // LAND
    totalLandBudget: calculateByType('landCost', 'BUDGET'),
    totalLandForecast: calculateByType('landCost', 'FORECAST'),
    totalLandBaseline: calculateByType('landCost', 'BASELINE'),
    totalLandPToolForecast: calculateByType('landCost', 'SUBMITTED_FORECAST'),
    totalLandReleasedApproved: calculateByType(
      'landCost',
      'RELEASED_APPROVED_BUDGET',
    ),
    totalLandActuals: calculateByType('landCost', 'ACTUALS'),

    // RETAIL COST
    totalRetailCostBudget: calculateByType('retailCost', 'BUDGET'),
    totalRetailCostForecast: calculateByType('retailCost', 'FORECAST'),
    totalRetailCostBaseline: calculateByType('retailCost', 'BASELINE'),
    totalRetailCostPToolForecast: calculateByType(
      'retailCost',
      'SUBMITTED_FORECAST',
    ),
    totalRetailCostReleasedApproved: calculateByType(
      'retailCost',
      'RELEASED_APPROVED_BUDGET',
    ),
    totalRetailCostActuals: calculateByType('retailCost', 'ACTUALS'),

    // PROPERTY INVESTMENT
    totalPropertyInvestmentBudget: calculateByType(
      'propertyInvestment',
      'BUDGET',
    ),
    totalPropertyInvestmentForecast: calculateByType(
      'propertyInvestment',
      'FORECAST',
    ),
    totalPropertyInvestmentBaseline: calculateByType(
      'propertyInvestment',
      'BASELINE',
    ),
    totalPropertyInvestmentPToolForecast: calculateByType(
      'propertyInvestment',
      'SUBMITTED_FORECAST',
    ),
    totalPropertyInvestmentReleasedApproved: calculateByType(
      'propertyInvestment',
      'RELEASED_APPROVED_BUDGET',
    ),
    totalPropertyInvestmentActuals: calculateByType(
      'propertyInvestment',
      'ACTUALS',
    ),

    // RETAIL INVESTMENT
    totalRetailInvestmentBudget: calculateByType('retailInvestment', 'BUDGET'),
    totalRetailInvestmentForecast: calculateByType(
      'retailInvestment',
      'FORECAST',
    ),
    totalRetailInvestmentBaseline: calculateByType(
      'retailInvestment',
      'BASELINE',
    ),
    totalRetailInvestmentPToolForecast: calculateByType(
      'retailInvestment',
      'SUBMITTED_FORECAST',
    ),
    totalRetailInvestmentReleasedApproved: calculateByType(
      'retailInvestment',
      'RELEASED_APPROVED_BUDGET',
    ),
    totalRetailInvestmentActuals: calculateByType(
      'retailInvestment',
      'ACTUALS',
    ),

    // TOTALS
    totalFinancialBudget: round2(
      (calculateByType('propertyInvestment', 'BUDGET') ||
        calculateByType('landCost', 'BUDGET')) +
        calculateByType('retailCost', 'BUDGET') +
        calculateByType('retailInvestment', 'BUDGET'),
    ),
    totalFinancialForecast: round2(
      (calculateByType('propertyInvestment', 'FORECAST') ||
        calculateByType('landCost', 'FORECAST')) +
        calculateByType('retailCost', 'FORECAST') +
        calculateByType('retailInvestment', 'FORECAST'),
    ),
    totalFinancialBaseline: round2(
      (calculateByType('propertyInvestment', 'BASELINE') ||
        calculateByType('landCost', 'BASELINE')) +
        calculateByType('retailCost', 'BASELINE') +
        calculateByType('retailInvestment', 'BASELINE'),
    ),
    totalFinancialPToolForecast: round2(
      (calculateByType('propertyInvestment', 'SUBMITTED_FORECAST') ||
        calculateByType('landCost', 'SUBMITTED_FORECAST')) +
        calculateByType('retailCost', 'SUBMITTED_FORECAST') +
        calculateByType('retailInvestment', 'SUBMITTED_FORECAST'),
    ),
    totalFinancialReleasedApproved: round2(
      (calculateByType('propertyInvestment', 'RELEASED_APPROVED_BUDGET') ||
        calculateByType('landCost', 'RELEASED_APPROVED_BUDGET')) +
        calculateByType('retailCost', 'RELEASED_APPROVED_BUDGET') +
        calculateByType('retailInvestment', 'RELEASED_APPROVED_BUDGET'),
    ),
    totalFinancialActuals: round2(
      (calculateByType('propertyInvestment', 'ACTUALS') ||
        calculateByType('landCost', 'ACTUALS')) +
        calculateByType('retailCost', 'ACTUALS') +
        calculateByType('retailInvestment', 'ACTUALS'),
    ),
  };
};

export const calculateAndSetTotals = (investments, setValue) => {
  if (!investments) {
    return;
  }

  const totals = returnTotals(investments);
  Object.keys(totals).forEach((key) => {
    setValue(key, totals[key]);
  });
};
